import { ShieldCheck, CreditCard, CalendarX, Users, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCmsContent } from "@/hooks/useCmsContent";

const BookingPolicy = () => {
  const { t } = useLanguage();
  const { get } = useCmsContent();
  const bp = t.bookingPolicyPage;

  return (
    <section className="hotel-section bg-card">
      <div className="hotel-container max-w-4xl">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
          <p className="hotel-caption text-accent mb-4">{get("bp_caption") || bp.caption}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light text-foreground mb-4 md:mb-6">{get("booking_policy_title") || bp.heading}</h2>
          <p className="hotel-body text-muted-foreground">{get("booking_policy_text") || bp.text}</p>
        </div>
        <div className="space-y-4 md:space-y-6">
          <div className="bg-background p-6 md:p-10 rounded-lg shadow-soft">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-accent/10 rounded-lg shrink-0"><CreditCard className="w-6 h-6 text-accent" /></div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-foreground mb-3">{get("bp_payment") || bp.payment}</h3>
                <p className="hotel-body text-muted-foreground">{get("bp_payment_text") || bp.paymentText}</p>
              </div>
            </div>
          </div>
          <div className="bg-background p-6 md:p-10 rounded-lg shadow-soft">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-accent/10 rounded-lg shrink-0"><CalendarX className="w-6 h-6 text-accent" /></div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-foreground mb-3">{get("bp_cancellation") || bp.cancellation}</h3>
                <p className="hotel-body text-muted-foreground mb-4">
                  {get("bp_cancellation_text") || bp.cancellationText}{" "}<strong className="text-foreground">{get("bp_cancellation_bold") || bp.cancellationBold}</strong>{" "}{get("bp_cancellation_text2") || bp.cancellationText2}
                </p>
                <p className="hotel-body text-muted-foreground">{get("bp_cancellation_noshow") || bp.cancellationNoShow}</p>
              </div>
            </div>
          </div>
          <div className="bg-background p-6 md:p-10 rounded-lg shadow-soft">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-accent/10 rounded-lg shrink-0"><Users className="w-6 h-6 text-accent" /></div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-foreground mb-3">{get("bp_group") || bp.groupBookings}</h3>
                <p className="hotel-body text-muted-foreground">
                  {get("bp_group_text") || bp.groupText}{" "}<strong className="text-foreground">{get("bp_group_bold") || bp.groupBold}</strong>{" "}{get("bp_group_text2") || bp.groupText2}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-primary/5 border border-primary/15 p-6 md:p-10 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg shrink-0"><AlertTriangle className="w-6 h-6 text-primary" /></div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-foreground mb-3">{get("bp_important") || bp.importantNote}</h3>
                <p className="hotel-body text-muted-foreground">
                  {get("bp_important_text") || bp.importantText}{" "}<strong className="text-foreground">{get("bp_important_bold") || bp.importantBold}</strong>{" "}{get("bp_important_text2") || bp.importantText2}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 md:mt-12 bg-background rounded-lg shadow-soft overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-accent" />
              <h3 className="font-display text-xl font-semibold text-foreground">{bp.overview}</h3>
            </div>
          </div>
          <div className="divide-y divide-border">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 px-4 md:px-6 py-3 md:py-4">
              <span className="font-body text-xs md:text-sm text-muted-foreground">{bp.singleFree}</span>
              <span className="font-body text-xs md:text-sm text-foreground font-medium">{bp.singleValue}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 px-4 md:px-6 py-3 md:py-4">
              <span className="font-body text-xs md:text-sm text-muted-foreground">{bp.groupFree}</span>
              <span className="font-body text-xs md:text-sm text-foreground font-medium">{bp.groupValue}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 px-4 md:px-6 py-3 md:py-4">
              <span className="font-body text-xs md:text-sm text-muted-foreground">{bp.lateNoShow}</span>
              <span className="font-body text-xs md:text-sm text-foreground font-medium">{bp.lateValue}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 px-4 md:px-6 py-3 md:py-4">
              <span className="font-body text-xs md:text-sm text-muted-foreground">{bp.paymentLabel}</span>
              <span className="font-body text-xs md:text-sm text-foreground font-medium">{bp.paymentValue}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingPolicy;
